// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuInfo } from 'rc-menu/lib/interface';

import { CombinedState } from 'reducers';
import AnnotationMenuComponent, { Actions } from 'components/annotation-page/top-bar/annotation-menu';
import { updateJobAsync } from 'actions/jobs-actions';
import {
    saveAnnotationsAsync,
    setForceExitAnnotationFlag as setForceExitAnnotationFlagAction,
    removeAnnotationsAsync as removeAnnotationsAsyncAction,
    changeFrameAsync,
} from 'actions/annotation-actions';
import { exportActions } from 'actions/export-actions';
import { importActions } from 'actions/import-actions';
import {
    Issue,
    Job, JobStage, JobState, Organization, User,
} from 'cvat-core-wrapper';
import { Modal, message } from 'antd';
import Text from 'antd/lib/typography/Text';
import { IssueType } from 'cvat-core/src/issue';

interface StateToProps {
    orgInstance: Organization | null | undefined;
    jobInstance: Job;
    user: User;
    stopFrame: number;
}

interface DispatchToProps {
    showExportModal: (jobInstance: Job) => void;
    showImportModal: (jobInstance: Job) => void;
    removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly: boolean): void;
    setForceExitAnnotationFlag(forceExit: boolean): void;
    saveAnnotations(jobInstance: Job, async :boolean, afterSave?: () => void): void;
    updateJob(jobInstance: Job): Promise<boolean>;
    changeFrame: (frame: number) => void;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: jobInstance,
                instance: { stopFrame },
            },
        },
        auth: {
            user,
        },
        organizations: {
            current: orgInstance,
        },
    } = state;

    return {
        orgInstance,
        jobInstance,
        user,
        stopFrame,
    };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        showExportModal(jobInstance: Job): void {
            dispatch(exportActions.openExportDatasetModal(jobInstance));
        },
        showImportModal(jobInstance: Job): void {
            dispatch(importActions.openImportDatasetModal(jobInstance));
        },
        removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly:boolean) {
            dispatch(removeAnnotationsAsyncAction(startnumber, endnumber, delTrackKeyframesOnly));
        },
        setForceExitAnnotationFlag(forceExit: boolean): void {
            dispatch(setForceExitAnnotationFlagAction(forceExit));
        },
        saveAnnotations(jobInstance: Job, async :boolean, afterSave?: () => void): void {
            dispatch(saveAnnotationsAsync(jobInstance, async, afterSave));
        },
        updateJob(jobInstance: Job): Promise<boolean> {
            return dispatch(updateJobAsync(jobInstance));
        },
        changeFrame(frame: number): void {
            dispatch(changeFrameAsync(frame));
        },
    };
}

function checkUnresolvedIssues(issues: Issue[], changeFrame: (frame: number) => void): boolean {
    for (const issue of issues) {
        if (issue.type === IssueType.CERTAIN && !issue.resolved) {
            changeFrame(issue.frame);

            Modal.warning({
                title: '尝试完成仍有未解决问题的作业',
                className: 'cvat-modal-unsupported-features-warning',
                content: (
                    <Text>
                        请先解决所有明确定义的问题！
                    </Text>
                ),
            });
            return true;
        }
    }
    return false;
}

type Props = StateToProps & DispatchToProps & RouteComponentProps;

function AnnotationMenuContainer(props: Props): JSX.Element {
    const {
        orgInstance,
        jobInstance,
        user,
        stopFrame,
        history,
        showExportModal,
        showImportModal,
        removeAnnotations,
        setForceExitAnnotationFlag,
        saveAnnotations,
        updateJob,
        changeFrame,
    } = props;

    const onClickMenu = (params: MenuInfo): void => {
        const [action] = params.keyPath;
        if (action === Actions.EXPORT_JOB_DATASET) {
            showExportModal(jobInstance);
        } else if (action === Actions.RENEW_JOB) {
            jobInstance.state = JobState.NEW;
            jobInstance.stage = JobStage.ANNOTATION;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    message.info('再次标注', 2);
                }
            });
        } else if (action === Actions.FINISH_ANNOTATION) {
            jobInstance.issues().then((issues) => {
                if (checkUnresolvedIssues(issues, changeFrame)) {
                    return;
                }

                jobInstance.stage = JobStage.VALIDATION;
                jobInstance.state = JobState.NEW;
                updateJob(jobInstance).then((success) => {
                    if (success) {
                        history.push('/jobs/'); // 标注员视角，应该跳转回作业列表页标注下一个作业
                    }
                }).catch((error) => {
                    console.error('无法更新作业:', error);
                });
            }).catch((error) => {
                console.error('无法获取问题:', error);
            });
        } else if (action === Actions.FINISH_VALIDATION) {
            jobInstance.issues().then((issues) => {
                if (checkUnresolvedIssues(issues, changeFrame)) {
                    return;
                }

                jobInstance.stage = JobStage.INSPECTION;
                jobInstance.state = JobState.NEW;
                updateJob(jobInstance).then((success) => {
                    if (success) {
                        history.push(`/tasks/${jobInstance.taskId}`); // 审核员视角，应该跳转回任务详情页审核下一个作业
                    }
                }).catch((error) => {
                    console.error('无法更新作业:', error);
                });
            }).catch((error) => {
                console.error('无法获取问题:', error);
            });
        } else if (action === Actions.REVERT_VALIDATION) {
            jobInstance.stage = JobStage.ANNOTATION;
            jobInstance.state = JobState.REJECTED;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    history.push(`/tasks/${jobInstance.taskId}`); // 审核员视角，应该跳转回任务详情页审核下一个作业
                }
            });
        } else if (action === Actions.FINISH_INSPECTION) {
            jobInstance.stage = JobStage.QUALITYCTL;
            jobInstance.state = JobState.NEW;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    history.push(`/tasks/${jobInstance.taskId}`); // 质检员视角，应该跳转回任务详情页审核下一个作业
                }
            }).catch((error) => {
                console.error('无法更新作业:', error);
            });
        } else if (action === Actions.FINISH_QUALITYCTL) {
            jobInstance.issues().then((issues) => {
                if (checkUnresolvedIssues(issues, changeFrame)) {
                    return;
                }

                jobInstance.stage = JobStage.ACCEPTANCE;
                jobInstance.state = JobState.COMPLETED;
                updateJob(jobInstance).then((success) => {
                    if (success) {
                        history.push(`/tasks/${jobInstance.taskId}`); // 质控员视角，应该跳转回任务详情页审核下一个作业
                    }
                }).catch((error) => {
                    console.error('无法更新作业:', error);
                });
            }).catch((error) => {
                console.error('无法获取问题:', error);
            });
        } else if (action === Actions.REVERT_INSPECTION) {
            jobInstance.stage = JobStage.VALIDATION;
            jobInstance.state = JobState.REJECTED;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    history.push(`/tasks/${jobInstance.taskId}`); // 质控员视角，应该跳转回任务详情页审核下一个作业
                }
            });
        } else if (action === Actions.REVERT_QUALITYCTL) {
            jobInstance.stage = JobStage.INSPECTION;
            jobInstance.state = JobState.REJECTED;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    history.push(`/tasks/${jobInstance.taskId}`); // 质控员视角，应该跳转回任务详情页审核下一个作业
                }
            });
        } else if (action === Actions.FINISH_ACCEPTANCE) {
            jobInstance.issues().then((issues) => {
                if (checkUnresolvedIssues(issues, changeFrame)) {
                    return;
                }

                jobInstance.stage = JobStage.ACCEPTANCE;
                jobInstance.state = JobState.COMPLETED;
                updateJob(jobInstance).then((success) => {
                    if (success) {
                        history.push(`/tasks/${jobInstance.taskId}`); // 验收员视角，应该跳转回任务详情页审核下一个作业
                    }
                }).catch((error) => {
                    console.error('无法更新作业:', error);
                });
            }).catch((error) => {
                console.error('无法获取问题:', error);
            });
        } else if (action === Actions.OPEN_TASK) {
            history.push(`/tasks/${jobInstance.taskId}`);
        } else if (action.startsWith('state:')) {
            [, jobInstance.state] = action.split(':') as [string, JobState];
            updateJob(jobInstance).then((success) => {
                if (success) {
                    message.info('作业状态更新', 2);
                }
            });
        } else if (action === Actions.LOAD_JOB_ANNO) {
            showImportModal(jobInstance);
        }
    };

    return (
        <AnnotationMenuComponent
            taskMode={jobInstance.mode}
            onClickMenu={onClickMenu}
            removeAnnotations={removeAnnotations}
            setForceExitAnnotationFlag={setForceExitAnnotationFlag}
            saveAnnotations={saveAnnotations}
            orgInstance={orgInstance}
            jobInstance={jobInstance}
            user={user}
            stopFrame={stopFrame}
        />
    );
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AnnotationMenuContainer));
